import { useEffect } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
// The LEAF subpath and not the package ROOT, which is a bundle constraint: this file is statically
// in the shell's chunk (App.tsx imports MessagesRoute statically), so the root would put
// `ProviderMessagesPanel` — the component the shell goes to the trouble of `React.lazy`-ing — into
// the chunk every authenticated member downloads. Reaching the key through `hooks/useProviderThreads`
// would cost nothing (the nav's unread dot already puts that module in this chunk), but a shared
// constant belongs in a module of its own; see the note in providerThreadsCache.ts.
import { PROVIDER_THREADS_CACHE_KEY, type ProviderThreadList } from "@internal/messages/provider-threads-cache"
import { trpc } from "#/lib/trpc/client"

/**
 * Re-resolve the member's Healthie identity ONCE on entry to the Clinicians tab, and refresh the
 * thread list if that turns one up.
 *
 * The problem it solves is a stale negative. The BFF caches "this member has no Healthie patient"
 * under `healthie-identity:${memberId}` for a minute, and `threadList` serves that cached answer as
 * `hasProviderIdentity: false` — which in turn stops `useProviderThreadsQuery`'s poll (see its
 * `refetchInterval`). So a member whose patient record is created upstream while they are on the page
 * stays looking at an empty tab that has also stopped asking: what is left is react-query's refetch
 * on mount and on window focus, so they recover on their next navigation back or return to the
 * browser, and then only once the BFF's cached negative has lapsed. Entering the tab is the
 * member-driven moment to re-ask, and closing that minute is the whole of what it buys.
 *
 * ⛔ ONCE PER ENTRY, and that is a constraint rather than an optimisation: next to nothing on the
 * server side bounds this. A negative the BFF re-checked in the last few seconds is served back
 * without a gateway call, and that is all — there is no single-flight and no rate limiter (both
 * deliberately out of scope on CONAPP-532), so every call past that floor is a Patients gateway
 * resolve. Never put this on a timer, a poll, or a per-render path. `ProvidersTabContent` is one
 * component instance across BOTH `/messages/providers` and
 * `/messages/providers/:thread` (react-router's `Switch` reuses the element rather than remounting it
 * for two routes rendering the same component), so selecting a thread does not re-enter the tab and
 * does not fire this again.
 *
 * A MUTATION though the caller reads nothing back: the call writes — the identity cache always, and
 * the Convex mirror on a newly found identity.
 *
 * `enabled` is the caller's gate, not a second flag read here. It is false while the program flags are
 * still loading, so the effect fires on the transition to a settled consumer and not before — asking
 * for a member we have not yet established is a consumer would spend a gateway resolve on the majority
 * of members who are not.
 *
 * A REJECTION IS DROPPED ON PURPOSE. `mutate` (rather than `mutateAsync`) swallows it, so a Patients
 * gateway outage leaves the tab rendering exactly as it did before this hook existed: `threadList`
 * runs its own resolve on the same page and owns the error state the member sees. There is nothing
 * here for them to act on and nothing to report that the failing request has not already reported.
 */
export function useEnsureProviderIdentity(enabled: boolean): void {
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: () => trpc.messages.refreshProviderIdentity.mutate(),
    // NO RETRIES — which changes nothing today and is here to keep it that way. React-query does not
    // retry a mutation by default and the app's QueryClient sets no `defaultOptions` (src/main.tsx),
    // so what this pins is the policy against a shared one arriving later: a status-aware helper
    // like `trpcRetry` only rules out the statuses it can reason about, so a 500 or a 502 would be
    // retried — a second unthrottled gateway resolve plus, if the first succeeded and only its
    // response was lost, a second `ensurePatientHydrated`. There is nothing to win either: the
    // member's own `threadList` request is resolving the same identity on the same page, so a
    // transient failure costs them nothing this could recover.
    retry: false,
    onSuccess: async ({ identityAdded }) => {
      // Only the TRANSITION refetches. A cached `found` and a re-confirmed `absent` both answer
      // false, and refetching the list for either would cost a BFF invocation to be handed back what
      // the tab is already showing.
      if (!identityAdded) {
        return
      }
      // ⚠ CANCEL FIRST, and it is not belt-and-braces — `invalidateQueries` ALONE loses the race
      // this hook exists to win. react-query v4 honours its own `cancelRefetch` only when the query
      // has resolved at least once (`Query.fetch` gates it on `state.dataUpdatedAt`); before that it
      // hands back the in-flight promise instead of starting a fetch. On a page load straight onto
      // /messages/providers the first `threadList` request is exactly that — still open, and built
      // from the cached `absent` this call has just replaced. Invalidating would adopt it, and when
      // it lands writing `hasProviderIdentity: false` over a member who now has one, which stops the
      // poll (useProviderThreads.ts) and leaves the empty tab this whole feature exists to unstick.
      await queryClient.cancelQueries(PROVIDER_THREADS_CACHE_KEY)
      await queryClient.invalidateQueries(PROVIDER_THREADS_CACHE_KEY)
    }
  })

  useEffect(() => {
    if (!enabled) {
      return
    }
    // WHAT THE SPA ALREADY KNOWS, IT DOES NOT ASK. The thread-list entry is app-wide and outlives
    // the tab — the nav's unread dot keeps it open — so a consumer who has used Clinicians this
    // session already has `hasProviderIdentity: true` sitting here. The refresh can only ever answer
    // `identityAdded: false` for them (the BFF short-circuits on a cached `found`), so the round
    // trip buys nothing. Only `=== true` skips: `undefined` is "we have not asked yet" and false is
    // the very state this hook is here to re-check.
    if (queryClient.getQueryData<ProviderThreadList>(PROVIDER_THREADS_CACHE_KEY)?.hasProviderIdentity === true) {
      return
    }
    mutate()
  }, [enabled, mutate, queryClient])
}
